/**
 * Admin API Module
 * CCH Care — Phase 2.5 → 5e
 * Last Modified: 2026-05-25
 *
 * Changes (2026-05-25):
 * - Staff: getStaff() now accepts { includeInactive, role } options.
 *   Zero-arg call still works (backwards compatible).
 * - Staff: new reactivateStaff(membershipId) — POSTs action=reactivate.
 *   Reordered Staff section so all verbs sit together as one-liners.
 *
 * Changes (2026-04-22):
 * - Added addFacility() for the multi-facility management feature
 *   (Option A). Creates a new facility under the current tenant.
 */
import client from './client';

// ── Setup Status ──
export const getSetupStatus = () => client.get('/admin/setup-status.php').then(r => r.data);

// ── Units ──
export const getUnits  = ()     => client.get('/admin/units.php').then(r => r.data);
export const createUnit = (data) => client.post('/admin/units.php', data).then(r => r.data);
export const updateUnit = (data) => client.put('/admin/units.php', data).then(r => r.data);
export const deleteUnit = (unitId) => client.delete(`/admin/units.php?unit_id=${unitId}`).then(r => r.data);

// ── Rooms ──
export const getRooms  = (unitId) => client.get('/admin/rooms.php', { params: { unit_id: unitId } }).then(r => r.data);
export const createRoom = (data)  => client.post('/admin/rooms.php', data).then(r => r.data);
export const updateRoom = (data)  => client.put('/admin/rooms.php', data).then(r => r.data);
export const deleteRoom = (roomId) => client.delete(`/admin/rooms.php?room_id=${roomId}`).then(r => r.data);

// ── Beds ──
export const getBeds  = (roomId) => client.get('/admin/beds.php', { params: { room_id: roomId } }).then(r => r.data);
export const createBed = (data)  => client.post('/admin/beds.php', data).then(r => r.data);
export const deleteBed = (bedId) => client.delete(`/admin/beds.php?bed_id=${bedId}`).then(r => r.data);

// ── Patients/Residents ──
export const getResidents   = ()           => client.get('/admin/patients.php').then(r => r.data);
export const getResident    = (patientId)  => client.get('/admin/patients.php', { params: { patient_id: patientId } }).then(r => r.data);
export const enrollResident = (data)       => client.post('/admin/patients.php', data).then(r => r.data);
export const updateResident = (data)       => client.put('/admin/patients.php', data).then(r => r.data);

// ── Standing Alerts ──
export const getStandingAlerts    = (patientId) => client.get('/admin/standing-alerts.php', { params: { patient_id: patientId } }).then(r => r.data);
export const createStandingAlert  = (data)      => client.post('/admin/standing-alerts.php', data).then(r => r.data);
export const updateStandingAlert  = (data)      => client.put('/admin/standing-alerts.php', data).then(r => r.data);
export const resolveStandingAlert = (alertId, reason) => client.delete(`/admin/standing-alerts.php?standing_alert_id=${alertId}&reason=${encodeURIComponent(reason || 'Resolved')}`).then(r => r.data);

// ── Care Shifts ──
export const getCareShifts   = (date) => client.get('/admin/care-shifts.php', { params: { date } }).then(r => r.data);
export const createCareShifts = (data) => client.post('/admin/care-shifts.php', data).then(r => r.data);
export const updateCareShift  = (data) => client.put('/admin/care-shifts.php', data).then(r => r.data);

// ── Shift Assignments ──
export const getShiftAssignments    = (params) => client.get('/admin/shift-assignments.php?action=shifts', { params }).then(r => r.data);
export const createShiftAssignment  = (data)   => client.post('/admin/shift-assignments.php?action=shifts', data).then(r => r.data);
export const deleteShiftAssignment  = (id)     => client.delete(`/admin/shift-assignments.php?action=shifts&shift_assignment_id=${id}`).then(r => r.data);

// ── Patient Assignments ──
export const getPatientAssignments = (assignmentId) => client.get('/admin/shift-assignments.php?action=patients', { params: { shift_assignment_id: assignmentId } }).then(r => r.data);
export const assignPatients        = (data)          => client.post('/admin/shift-assignments.php?action=patients', data).then(r => r.data);
export const unassignPatient       = (id)            => client.delete(`/admin/shift-assignments.php?action=patients&id=${id}`).then(r => r.data);

// ── Staff ──
// getStaff accepts { includeInactive, role } options. Zero-arg is fine.
export const getStaff = (opts = {}) => client.get('/admin/staff.php', {
  params: {
    include_inactive: opts.includeInactive ? 1 : undefined,
    role: opts.role || undefined,
  },
}).then(r => r.data);

export const inviteStaff        = (data)         => client.post('/admin/staff.php', data).then(r => r.data);
export const updateStaff        = (data)         => client.put('/admin/staff.php', data).then(r => r.data);
export const deactivateStaff    = (membershipId) => client.delete(`/admin/staff.php?membership_id=${membershipId}`).then(r => r.data);
export const reactivateStaff    = (membershipId) => client.post('/admin/staff.php', { action: 'reactivate', membership_id: membershipId }).then(r => r.data);
export const resetStaffPassword = (membershipId) => client.post('/admin/staff.php', { action: 'reset_password', membership_id: membershipId }).then(r => r.data);

// ── Organization & Facility Settings ──
export const getSettings            = ()     => client.get('/admin/settings.php?action=get').then(r => r.data);
export const updateOrgSettings      = (data) => client.post('/admin/settings.php?action=update_org', data).then(r => r.data);
export const updateFacilitySettings = (data) => client.post('/admin/settings.php?action=update_facility', data).then(r => r.data);
export const addFacility            = (data) => client.post('/admin/settings.php?action=add_facility', data).then(r => r.data);

// ── Medications ──
export const getMedications    = (patientId, includeInactive = false) =>
  client.get(`/adl/medications.php?action=list&patient_id=${patientId}${includeInactive ? '&include_inactive=1' : ''}`).then(r => r.data);
export const addMedication     = (data) => client.post('/adl/medications.php?action=add', data).then(r => r.data);
export const updateMedication  = (data) => client.post('/adl/medications.php?action=update', data).then(r => r.data);
export const discontinueMed    = (data) => client.post('/adl/medications.php?action=discontinue', data).then(r => r.data);
export const getMedResidents   = ()     => client.get('/adl/medications.php?action=residents').then(r => r.data);

// ── MAR ──
export const getMARToday          = (patientId)          => client.get(`/adl/mar.php?action=today&patient_id=${patientId}`).then(r => r.data);
export const documentMAR          = (data)               => client.post('/adl/mar.php?action=administer', data).then(r => r.data);
export const documentPRN          = (data)               => client.post('/adl/mar.php?action=prn', data).then(r => r.data);
export const getMARHistory        = (patientId, days = 7) => client.get(`/adl/mar.php?action=history&patient_id=${patientId}&days=${days}`).then(r => r.data);
export const documentPRNFollowup  = (data)               => client.post('/adl/mar.php?action=followup', data).then(r => r.data);